/**
 * **Goals, their next step, and who a task names** (Personal build step 4; F372, and Thomas, 2026-10-18: *Todoist read
 * for the Personal profile, to-dos with dates and priority, linked to people, orgs and assets where the task names
 * them*).
 *
 * Rules, and nothing else: no model is called here. A goal is what the person typed. Its next step is the earliest
 * dated open thing linked to it, which is what *Needs you* raises when it is due. *Names* is read from the task's one
 * line by matching the names Autho already holds — a person on a card, an organisation a correspondent signs with, an
 * asset the person typed — and a match is a suggestion, `inferred`, never a link the person made.
 *
 * Imported under strip-only type stripping (F123): no parameter properties, no enums.
 */
import type { Attention, AttentionKind } from './attention.ts';
import { DUE_SOON_DAYS } from './attention.ts';
import { parseDay, parseName, parseText } from './holdings.ts';
import type { Parsed } from './holdings.ts';

/** A goal's states, as `Goals.md` names them. Only an active goal raises anything. */
export const GOAL_STATES = ['active', 'paused', 'achieved', 'abandoned'] as const;
export type GoalState = (typeof GOAL_STATES)[number];

export const GOAL_WHY_MAX = 2000;
/** The earliest horizon the database takes; a goal set before it is a typing slip. */
export const GOAL_HORIZON_MIN = '2000-01-01';

export interface GoalFields {
  title: string;
  why: string | null;
  horizon: string | null;
  state: GoalState;
}

/**
 * What the person typed, made into a goal or refused with the reason, in the words step 3's forms already use: a
 * title is a name (required, at most 200 characters), *why* is free text, a horizon is a calendar day. Blank optional
 * fields are absent rather than empty, so a goal never says *why: ""*.
 */
export function goalFields(input: {
  title: unknown;
  why?: unknown;
  horizon?: unknown;
  state?: unknown;
}): Parsed<GoalFields> {
  const title = parseName(input.title);
  if (!title.ok) return title;
  const why = parseText(input.why, GOAL_WHY_MAX);
  if (!why.ok) return why;
  const horizon = parseDay(input.horizon);
  if (!horizon.ok) return horizon;
  if (horizon.value !== null && horizon.value < GOAL_HORIZON_MIN)
    return { ok: false, error: 'invalid_date' };
  const state = input.state ?? 'active';
  if (!(GOAL_STATES as readonly unknown[]).includes(state))
    return { ok: false, error: 'kind_unknown' };
  return {
    ok: true,
    value: {
      title: title.value,
      why: why.value,
      horizon: horizon.value,
      state: state as GoalState,
    },
  };
}

/** One thing a goal is waiting on, as the goal's screen and *Needs you* read it. */
export interface GoalStep {
  kind: 'commitment' | 'task';
  id: string;
  title: string;
  /** The day it is due, in the profile's own time zone. Null when nothing was dated. */
  dueDate: string | null;
  /** False when it is done, forgotten, or gone from the list it came from. */
  open: boolean;
}

/**
 * **The goal's next step**: the open step due soonest, and among the undated ones the first linked. Null when nothing
 * linked is open — a goal with nothing to do next is a goal to look at, which the screen says.
 */
export function nextStep<T extends GoalStep>(steps: readonly T[]): T | null {
  const open = steps.filter((s) => s.open);
  const dated = open
    .filter((s) => s.dueDate !== null)
    .sort((a, b) => ((a.dueDate as string) < (b.dueDate as string) ? -1 : 1));
  return dated[0] ?? open[0] ?? null;
}

const dayNumber = (day: string): number => Math.floor(Date.parse(`${day}T00:00:00Z`) / 86_400_000);

/** A Todoist task linked to an active goal, as *Needs you* reads it. */
export interface GoalTask {
  id: string;
  content: string;
  dueDate: string | null;
  url: string;
  goalId: string;
  goalTitle: string;
}

/** An item raised by a goal's task: the commitment rules' kinds, with the goal it serves and where to open it. */
export interface GoalTaskAttention extends Attention {
  taskId: string;
  goalId: string;
  goalTitle: string;
  link: string;
}

/**
 * **A goal's task that is due** (step 4's *see it on Needs you when its next step is due*). Only a dated task linked
 * to an active goal is raised — the whole of a Todoist list is not a reason to open Autho — as *overdue* the day after
 * its date and *due soon* inside the same working week the commitments use. Days are counted in the profile's own
 * calendar, so `today` is the caller's: a task due today is due today in Oslo, whatever the server's clock says.
 */
export function goalTaskAttention(input: {
  tasks: readonly GoalTask[];
  today: string;
}): GoalTaskAttention[] {
  const today = dayNumber(input.today);
  const out: GoalTaskAttention[] = [];
  const seen = new Set<string>();
  for (const task of input.tasks) {
    if (task.dueDate === null || seen.has(task.id)) continue;
    const due = dayNumber(task.dueDate);
    const kind: AttentionKind | null =
      due < today ? 'overdue' : due - today <= DUE_SOON_DAYS ? 'due_soon' : null;
    if (kind === null) continue;
    // One line per task, however many goals it serves: the first goal linked names it.
    seen.add(task.id);
    out.push({
      id: `${kind}:task:${task.id}`,
      kind,
      rule: `attention:${kind} · goal:task`,
      text: task.content,
      who: null,
      at: new Date(`${task.dueDate}T00:00:00Z`),
      days: Math.max(0, today - due),
      basis: [],
      commitmentId: null,
      personId: null,
      taskId: task.id,
      goalId: task.goalId,
      goalTitle: task.goalTitle,
      link: task.url,
    });
  }
  return out;
}

/** Something a task's line may name: a person on a card, an organisation, an asset the person typed. */
export interface Nameable {
  kind: 'person' | 'organisation' | 'asset';
  id: string;
  name: string;
}

export interface Mention {
  kind: Nameable['kind'];
  id: string;
  name: string;
  /** What in the line matched: the whole name, or a person's first name that no other person shares. */
  by: 'name' | 'first_name';
}

/** Shorter than this, a name is a word that happens to match (`AS`, `Bo`): never read as a mention. */
export const MENTION_MIN = 3;

/** The legal-form words a company is written with and without. */
const LEGAL_FORMS = new Set([
  'as',
  'asa',
  'ans',
  'da',
  'enk',
  'sa',
  'nuf',
  'ab',
  'oy',
  'ltd',
  'gmbh',
]);

const fold = (text: string): string =>
  text.normalize('NFC').toLocaleLowerCase('nb-NO').replace(/\s+/g, ' ').trim();

const escape = (text: string): string => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Whether `needle` stands in `haystack` as whole words: *Kari* in *Ring Kari*, never in *Karin*. */
function standsIn(haystack: string, needle: string): boolean {
  return new RegExp(`(?<![\\p{L}\\p{N}])${escape(needle)}(?![\\p{L}\\p{N}])`, 'u').test(haystack);
}

/** The ways an organisation is written: as typed, and without its legal form. */
function organisationForms(name: string): string[] {
  const words = fold(name).split(' ');
  const last = words[words.length - 1] as string;
  return words.length > 1 && LEGAL_FORMS.has(last.replace(/\./g, ''))
    ? [words.join(' '), words.slice(0, -1).join(' ')]
    : [words.join(' ')];
}

/**
 * **Who and what a task's line names**, among the names Autho holds, in the order they are listed. A whole name
 * always counts; a person's first name counts only when no other person shares it, because *Ring Kari* with two
 * Karis is a guess. Nothing shorter than {@link MENTION_MIN} letters is read as a name.
 */
export function mentionsIn(line: string, candidates: readonly Nameable[]): Mention[] {
  const text = fold(line);
  const firstNames = new Map<string, number>();
  for (const c of candidates) {
    if (c.kind !== 'person') continue;
    const first = fold(c.name).split(' ')[0] as string;
    firstNames.set(first, (firstNames.get(first) ?? 0) + 1);
  }
  const out: Mention[] = [];
  const seen = new Set<string>();
  for (const c of candidates) {
    const key = `${c.kind}:${c.id}`;
    if (seen.has(key)) continue;
    const forms = c.kind === 'organisation' ? organisationForms(c.name) : [fold(c.name)];
    const whole = forms.some((f) => f.length >= MENTION_MIN && standsIn(text, f));
    const words = fold(c.name).split(' ');
    const first = words[0] as string;
    const byFirst =
      !whole &&
      c.kind === 'person' &&
      words.length > 1 &&
      first.length >= MENTION_MIN &&
      firstNames.get(first) === 1 &&
      standsIn(text, first);
    if (!whole && !byFirst) continue;
    seen.add(key);
    out.push({ kind: c.kind, id: c.id, name: c.name, by: whole ? 'name' : 'first_name' });
  }
  return out;
}

/**
 * Todoist's priority, as Todoist's own apps show it: the API's 4 is *P1*, the most urgent, and its 1 is *P4*, the
 * default. Said the way the person sees it in Todoist, so the two screens never disagree about what is urgent.
 */
export function todoistPriorityLabel(priority: 1 | 2 | 3 | 4): 'P1' | 'P2' | 'P3' | 'P4' {
  return `P${5 - priority}` as 'P1' | 'P2' | 'P3' | 'P4';
}
